import { useMemo } from "react";
import { useSnapshot } from "valtio/react";
import { listsState } from "@/states.ts";
import { useRoute } from "wouter";
import { TodoList } from "@/api/types";
import { useIsMobile } from "@/hooks/use-mobile.tsx";

export const useList = (listId: string) => {
	const { lists } = useSnapshot(listsState);
	return useMemo(() => lists.find((l) => l.id === listId), [lists, listId]);
};

export const useSortedLists = (lists: TodoList[]) => {
	return useMemo(() => {
		return [...lists].sort((a, b) => {
			const orderComparison = (a.order || 0) - (b.order || 0);
			if (orderComparison === 0) {
				return (
					new Date(b.created_at).getTime() -
					new Date(a.created_at).getTime()
				);
			}
			return orderComparison;
		});
	}, [lists]);
};

export const useCurrentList = (ignoreMobile: boolean) => {
	const { lists } = useSnapshot(listsState);
	const [match, params] = useRoute("/lists/:listId/items");
	const sortedLists = useSortedLists(lists as TodoList[]);
	const isMobile = useIsMobile();

	return useMemo(() => {
		if (match && params?.listId) {
			return sortedLists.find((l) => l.id === params.listId);
		}

		if (!ignoreMobile && isMobile !== false) {
			return null;
		}

		if (sortedLists.length <= 0) {
			return null;
		}

		return sortedLists[0];
	}, [sortedLists, match, params?.listId, isMobile, ignoreMobile]);
};
